import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth-store";
import { getBatchPresignedUrls, uploadToS3, completeUploads } from "@/api/uploads";
import { generateTrial, getTrialResult, getTrialStatus } from "@/api/trial";
import type { TrialGeneratedImage, TrialResultResponse } from "@/api/trial";
import { event as gaEvent } from "@/lib/gtag";
import { useTrialResultPolling } from "./useTrialResultPolling";

const TRIAL_STYLE = "classic-studio";
const START_RECOVERY_ATTEMPTS = 3;
const START_RECOVERY_INTERVAL_MS = 1_000;

type Step = "upload" | "generating" | "result" | "already-used";

interface PhotoSlot {
  label: string;
  category: "groom" | "bride";
  file: File | null;
  preview: string | null;
}

interface TrialAttemptBaseline {
  established: boolean;
  orderIdx: number | null;
}

const S3_HOST = "https://spring-ai-studio.s3.ap-northeast-2.amazonaws.com";
const toImageUrl = (urlOrKey: string) => {
  if (!urlOrKey) return "";
  if (urlOrKey.startsWith("http")) return urlOrKey;
  return `${S3_HOST}/${urlOrKey}`;
};

const waitForRecoveryInterval = (signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }

    const handleAbort = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", handleAbort);
      resolve();
    };
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", handleAbort);
      resolve();
    }, START_RECOVERY_INTERVAL_MS);

    signal.addEventListener("abort", handleAbort, { once: true });
    if (signal.aborted) handleAbort();
  });

const captureTrialAttemptBaseline = async (): Promise<TrialAttemptBaseline> => {
  try {
    const result = await getTrialResult();
    return { established: true, orderIdx: result.orderIdx };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return { established: true, orderIdx: null };
    }

    return { established: false, orderIdx: null };
  }
};

export default function TrialPage() {
  const navigate = useNavigate();
  const { accessToken } = useAuthStore();

  const [step, setStep] = useState<Step>("upload");
  const [photos, setPhotos] = useState<PhotoSlot[]>([
    { label: "신랑 사진 1", category: "groom", file: null, preview: null },
    { label: "신랑 사진 2", category: "groom", file: null, preview: null },
    { label: "신부 사진 1", category: "bride", file: null, preview: null },
    { label: "신부 사진 2", category: "bride", file: null, preview: null },
  ]);
  const [isUploading, setIsUploading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [pollingOrderIdx, setPollingOrderIdx] = useState<number | null>(null);
  const [resultImages, setResultImages] = useState<TrialGeneratedImage[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resultUnavailable, setResultUnavailable] = useState(false);
  const startRecoveryControllerRef = useRef<AbortController | null>(null);

  const showUnavailableResult = useCallback(() => {
    setIsPolling(false);
    setPollingOrderIdx(null);
    setResultImages([]);
    setErrorMessage(null);
    setResultUnavailable(true);
    setStep("already-used");
  }, []);

  const handlePollingCompleted = useCallback((result: TrialResultResponse) => {
    if (result.generatedImages.length === 0) {
      showUnavailableResult();
      return;
    }

    gaEvent("trial_complete", { style: TRIAL_STYLE });
    setResultImages(result.generatedImages);
    setResultUnavailable(false);
    setStep("result");
    setIsPolling(false);
    setPollingOrderIdx(null);
  }, [showUnavailableResult]);

  const handlePollingFailed = useCallback((result: TrialResultResponse) => {
    setResultImages([]);
    setResultUnavailable(false);
    setErrorMessage(
      result.errorMessage || "이미지 생성에 실패했습니다. 다시 시도해주세요.",
    );
    setStep("upload");
    setIsPolling(false);
    setPollingOrderIdx(null);
  }, []);

  const {
    progress,
    hasConnectionIssue,
    isLongWaiting,
    reset: resetPollingState,
  } =
    useTrialResultPolling({
      active: isPolling,
      expectedOrderIdx: pollingOrderIdx,
      onCompleted: handlePollingCompleted,
      onFailed: handlePollingFailed,
    });

  useEffect(() => {
    return () => {
      startRecoveryControllerRef.current?.abort();
    };
  }, []);

  // 로그인 체크
  useEffect(() => {
    if (!accessToken) {
      navigate("/login", { state: { from: "/trial" } });
    }
  }, [accessToken, navigate]);

  // 체험 가능 여부 확인 + 스타일 로드
  useEffect(() => {
    if (!accessToken) return;

    let cancelled = false;

    const restoreTrial = async () => {
      try {
        const status = await getTrialStatus();
        if (cancelled) return;

        const order = status.trialOrder;
        if (order?.status === "processing") {
          setErrorMessage(null);
          setResultUnavailable(false);
          setStep("generating");
          setPollingOrderIdx(order.orderIdx);
          setIsPolling(true);
          return;
        }

        if (order?.status === "completed") {
          if (order.generatedImages.length === 0) {
            showUnavailableResult();
          } else {
            setErrorMessage(null);
            setResultUnavailable(false);
            setResultImages(order.generatedImages);
            setStep("already-used");
            setPollingOrderIdx(null);
          }
          return;
        }

        if (order?.status === "cancelled" || order?.status === "failed") {
          setResultImages([]);
          setResultUnavailable(false);
          setPollingOrderIdx(null);
          setErrorMessage(
            "이전 이미지 생성에 실패했습니다. 사진을 확인한 뒤 다시 시도해주세요.",
          );
          setStep("upload");
          return;
        }

        if (status.available) {
          try {
            const result = await getTrialResult();
            if (cancelled) return;

            if (result.status === "processing") {
              setErrorMessage(null);
              setResultUnavailable(false);
              setStep("generating");
              setPollingOrderIdx(result.orderIdx);
              setIsPolling(true);
            } else if (result.status === "completed") {
              if (result.generatedImages.length === 0) {
                showUnavailableResult();
              } else {
                setErrorMessage(null);
                setResultUnavailable(false);
                setResultImages(result.generatedImages);
                setStep("already-used");
                setPollingOrderIdx(null);
              }
            } else if (result.status === "failed") {
              setResultImages([]);
              setResultUnavailable(false);
              setPollingOrderIdx(null);
              setErrorMessage(
                result.errorMessage ||
                  "이전 이미지 생성에 실패했습니다. 다시 시도해주세요.",
              );
              setStep("upload");
            }
          } catch {
            // 체험 이력이 없는 신규 사용자는 결과 조회가 실패할 수 있습니다.
          }
        }
      } catch {
        // 상태 확인 실패 시 업로드 화면을 유지합니다.
      }
    };

    void restoreTrial();

    return () => {
      cancelled = true;
    };
  }, [accessToken, showUnavailableResult]);

  const handleFileChange = useCallback(
    (index: number, file: File | null) => {
      setPhotos((prev) => {
        const next = [...prev];
        if (prev[index].preview) URL.revokeObjectURL(prev[index].preview!);
        next[index] = {
          ...prev[index],
          file,
          preview: file ? URL.createObjectURL(file) : null,
        };
        return next;
      });
    },
    [],
  );

  const allPhotosReady = photos.every((p) => p.file !== null);

  const recoverAmbiguousStart = useCallback(
    async (
      signal: AbortSignal,
      baseline: TrialAttemptBaseline,
    ): Promise<boolean> => {
      const belongsToNewAttempt = (orderIdx: number) =>
        baseline.established &&
        (baseline.orderIdx === null || orderIdx > baseline.orderIdx);

      for (let attempt = 0; attempt < START_RECOVERY_ATTEMPTS; attempt += 1) {
        if (attempt > 0) await waitForRecoveryInterval(signal);
        if (signal.aborted) return true;

        try {
          const result = await getTrialResult();
          if (signal.aborted) return true;

          if (
            belongsToNewAttempt(result.orderIdx) &&
            result.status === "processing"
          ) {
            setPollingOrderIdx(result.orderIdx);
            setIsPolling(true);
            return true;
          }

          if (
            belongsToNewAttempt(result.orderIdx) &&
            result.status === "completed"
          ) {
            handlePollingCompleted(result);
            return true;
          }

          if (
            belongsToNewAttempt(result.orderIdx) &&
            result.status === "failed"
          ) {
            handlePollingFailed(result);
            return true;
          }
        } catch {
          // 주문 커밋 전에는 결과 API가 일시적으로 404를 반환할 수 있습니다.
        }

        if (signal.aborted) return true;

        try {
          const status = await getTrialStatus();
          if (signal.aborted) return true;

          const order = status.trialOrder;
          if (
            order &&
            belongsToNewAttempt(order.orderIdx) &&
            order.status === "processing"
          ) {
            setPollingOrderIdx(order.orderIdx);
            setIsPolling(true);
            return true;
          }

          if (
            order &&
            belongsToNewAttempt(order.orderIdx) &&
            order.status === "completed"
          ) {
            handlePollingCompleted({
              orderIdx: order.orderIdx,
              status: "completed",
              progress: 100,
              generatedImages: order.generatedImages,
              errorMessage: null,
            });
            return true;
          }

          if (
            order &&
            belongsToNewAttempt(order.orderIdx) &&
            (order.status === "cancelled" || order.status === "failed")
          ) {
            handlePollingFailed({
              orderIdx: order.orderIdx,
              status: "failed",
              progress: 0,
              generatedImages: [],
              errorMessage:
                "이미지 생성에 실패했습니다. 사진을 확인한 뒤 다시 시도해주세요.",
            });
            return true;
          }
        } catch {
          // status도 확정 상태를 주지 못하면 다음 bounded 시도에서 재확인합니다.
        }
      }

      return false;
    },
    [handlePollingCompleted, handlePollingFailed],
  );

  const startGenerate = useCallback(
    async (idxs: number[]) => {
      startRecoveryControllerRef.current?.abort();
      const recoveryController = new AbortController();
      startRecoveryControllerRef.current = recoveryController;

      resetPollingState();
      setStep("generating");
      setIsPolling(false);
      setPollingOrderIdx(null);
      setResultUnavailable(false);
      setErrorMessage(null);

      const baseline = await captureTrialAttemptBaseline();
      if (recoveryController.signal.aborted) return;

      if (!baseline.established) {
        startRecoveryControllerRef.current = null;
        setErrorMessage(
          "현재 체험 상태를 확인하지 못했습니다. 잠시 후 다시 시도해주세요.",
        );
        setStep("upload");
        return;
      }

      try {
        const generateResponse = await generateTrial(idxs, TRIAL_STYLE);
        if (recoveryController.signal.aborted) return;

        startRecoveryControllerRef.current = null;
        setPollingOrderIdx(generateResponse.orderIdx);
        setIsPolling(true);
      } catch (error) {
        if (recoveryController.signal.aborted) return;

        if (axios.isAxiosError(error) && !error.response) {
          const recovered = await recoverAmbiguousStart(
            recoveryController.signal,
            baseline,
          );
          if (recoveryController.signal.aborted) return;

          startRecoveryControllerRef.current = null;
          if (recovered) return;
        }

        startRecoveryControllerRef.current = null;
        setPollingOrderIdx(null);
        setErrorMessage(
          "이미지 생성을 시작하지 못했습니다. 잠시 후 다시 시도해주세요.",
        );
        setStep("upload");
      }
    },
    [recoverAmbiguousStart, resetPollingState],
  );

  const handleUploadAndNext = useCallback(async () => {
    if (!allPhotosReady) return;
    gaEvent('trial_generate', { style: TRIAL_STYLE });
    resetPollingState();
    setIsUploading(true);
    setErrorMessage(null);

    try {
      const files = photos.map((p) => ({
        fileName: p.file!.name,
        mimeType: p.file!.type,
        fileSize: p.file!.size,
        photoCategory: p.category as "bride" | "groom",
      }));

      // 1. Presigned URL 발급
      const batch = await getBatchPresignedUrls(files);

      // 2. S3 업로드
      await Promise.all(
        batch.uploads.map((u, i) => uploadToS3(u.presignedUrl, photos[i].file!)),
      );

      // 3. 완료 알림
      const idxs = batch.uploads.map((u) => u.uploadIdx);
      await completeUploads(idxs);

      // 업로드 완료 후 바로 생성 시작
      await startGenerate(idxs);
    } catch {
      setErrorMessage("사진 업로드 중 오류가 발생했습니다. 다시 시도해주세요.");
    } finally {
      setIsUploading(false);
    }
  }, [allPhotosReady, photos, resetPollingState, startGenerate]);

  // --- Render ---
  return (
    <div className="min-h-screen bg-[#FDFAF6]">
      {/* Header */}
      <header className="bg-white border-b border-[#F0E8DC]">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <i className="ri-arrow-left-line text-lg"></i>
            <span className="text-sm">홈으로</span>
          </button>
          <h1 className="text-lg font-serif font-medium text-[#2D2D2D]">
            무료 체험
          </h1>
          <div className="w-16" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {/* Step indicator */}
        {step !== "already-used" && step !== "result" && (
          <div className="flex items-center justify-center gap-3 mb-10">
            {(["upload", "generating"] as Step[]).map((s, i) => {
              const labels = ["사진 업로드", "이미지 생성"];
              const stepIndex = ["upload", "generating"].indexOf(step);
              const isActive = i <= stepIndex;
              return (
                <div key={s} className="flex items-center gap-3">
                  {i > 0 && (
                    <div className={`w-8 h-px ${isActive ? "bg-[#C9A96E]" : "bg-gray-200"}`} />
                  )}
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium ${
                        isActive
                          ? "bg-[#C9A96E] text-white"
                          : "bg-gray-200 text-gray-400"
                      }`}
                    >
                      {i + 1}
                    </div>
                    <span
                      className={`text-xs ${isActive ? "text-[#2D2D2D] font-medium" : "text-gray-400"}`}
                    >
                      {labels[i]}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* STEP: Upload */}
        {step === "upload" && (
          <div className="bg-white rounded-2xl border border-[#F0E8DC] p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-[#FDF6EC] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-camera-line text-3xl text-[#C9A96E]"></i>
              </div>
              <h2 className="font-serif text-2xl font-medium text-[#2D2D2D] mb-2">
                사진을 업로드해주세요
              </h2>
              <p className="text-sm text-gray-500 font-light">
                신랑 2장, 신부 2장 — 총 4장의 정면 사진이 필요합니다
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {photos.map((slot, index) => (
                <label
                  key={index}
                  className={`relative aspect-[4/5] rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all overflow-hidden ${
                    slot.preview
                      ? "border-[#C9A96E] bg-[#FDFAF6]"
                      : "border-gray-200 hover:border-[#C9A96E] bg-gray-50"
                  }`}
                >
                  {slot.preview ? (
                    <>
                      <img
                        src={slot.preview}
                        alt={slot.label}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white text-xs">변경하기</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <i className="ri-add-line text-2xl text-gray-300 mb-2"></i>
                      <span className="text-xs text-gray-400">{slot.label}</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) =>
                      handleFileChange(index, e.target.files?.[0] || null)
                    }
                  />
                </label>
              ))}
            </div>

            {/* Photo Examples */}
            <div className="border border-gray-200 rounded-2xl p-6 bg-white mb-6">
              <p className="text-sm font-medium text-[#2D2D2D] mb-5 text-center">
                이런 사진을 올려주세요!
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Bad Example */}
                <div>
                  <div className="relative rounded-xl overflow-hidden border-2 border-red-300 bg-red-50">
                    <img
                      src="https://spring-ai-studio.s3.ap-northeast-2.amazonaws.com/spring-ai-studio/uploads/3/Gemini_Generated_Image_zdriwozdriwozdri.png"
                      alt="안좋은 예시 사진"
                      className="w-full aspect-[4/5] object-cover opacity-80"
                    />
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-red-500 text-white text-xs font-medium px-3 py-1.5 rounded-full">
                      <i className="ri-close-circle-line text-sm"></i>
                      안좋은 예
                    </div>
                  </div>
                  <ul className="mt-3 text-xs text-red-500 space-y-1 font-light px-1">
                    <li className="flex items-start gap-1.5">
                      <i className="ri-close-line mt-0.5 shrink-0"></i>
                      배경이 많이 나오는 사진
                    </li>
                    <li className="flex items-start gap-1.5">
                      <i className="ri-close-line mt-0.5 shrink-0"></i>
                      어둡거나 흐린 사진
                    </li>
                  </ul>
                </div>

                {/* Good Example */}
                <div>
                  <div className="relative rounded-xl overflow-hidden border-2 border-green-300 bg-green-50">
                    <img
                      src="https://spring-ai-studio.s3.ap-northeast-2.amazonaws.com/spring-ai-studio/uploads/3/Gemini_Generated_Image_z67od6z67od6z67o.png"
                      alt="좋은 예시 사진"
                      className="w-full aspect-[4/5] object-cover"
                    />
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-green-500 text-white text-xs font-medium px-3 py-1.5 rounded-full">
                      <i className="ri-checkbox-circle-line text-sm"></i>
                      좋은 예
                    </div>
                  </div>
                  <ul className="mt-3 text-xs text-green-600 space-y-1 font-light px-1">
                    <li className="flex items-start gap-1.5">
                      <i className="ri-check-line mt-0.5 shrink-0"></i>
                      얼굴이 정면에 잘 보이는 사진
                    </li>
                    <li className="flex items-start gap-1.5">
                      <i className="ri-check-line mt-0.5 shrink-0"></i>
                      밝고 선명한 사진 (정면 1장 + 옆면 1장 BEST)
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {errorMessage && (
              <p className="text-center text-sm text-red-500 mb-4">{errorMessage}</p>
            )}

            <button
              onClick={handleUploadAndNext}
              disabled={!allPhotosReady || isUploading}
              className="w-full py-4 bg-[#C9A96E] text-white rounded-full font-medium transition-all hover:bg-[#b8935a] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  업로드 중...
                </>
              ) : (
                <>
                  <i className="ri-sparkling-line"></i>
                  클래식 스튜디오 스타일로 생성하기
                </>
              )}
            </button>
          </div>
        )}

        {/* STEP: Generating */}
        {step === "generating" && (
          <div className="bg-white rounded-2xl border border-[#F0E8DC] p-12 text-center">
            <div className="w-24 h-24 bg-[#FDF6EC] rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <i className="ri-sparkling-line text-5xl text-[#C9A96E] animate-spin"></i>
            </div>
            <h2 className="font-serif text-2xl font-medium text-[#2D2D2D] mb-3">
              AI가 이미지를 생성하고 있습니다
            </h2>
            <p className="text-sm text-gray-500 font-light mb-8">
              최신 AI 기술로 당신만의 웨딩 사진을 만들고 있어요.<br />
              잠시만 기다려주세요.
            </p>
            <div className="max-w-xs mx-auto">
              <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                <span>생성 중...</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div
                  className="bg-[#C9A96E] h-2 rounded-full transition-all duration-700"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
            {isLongWaiting && (
              <p className="text-sm text-gray-500 font-light mt-6">
                평소보다 시간이 걸리고 있습니다. 페이지를 나가도 이미지 생성은
                계속됩니다.
              </p>
            )}
            {hasConnectionIssue && (
              <p className="text-sm text-amber-600 font-light mt-3">
                연결이 불안정합니다. 네트워크 연결을 확인해주세요. 생성 작업은
                계속 진행됩니다.
              </p>
            )}
          </div>
        )}

        {/* STEP: Result */}
        {step === "result" && (
          <div className="bg-white rounded-2xl border border-[#F0E8DC] p-8 text-center">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-check-double-line text-3xl text-green-500"></i>
            </div>
            <h2 className="font-serif text-2xl font-medium text-[#2D2D2D] mb-2">
              체험 이미지가 완성되었습니다!
            </h2>
            <p className="text-sm text-gray-500 font-light mb-8">
              마음에 드시나요? 더 다양한 스타일과 고품질 결과물은 유료 플랜에서 만나보세요.
            </p>

            <div className="flex justify-center gap-4 mb-8">
              {resultImages.map((img) => (
                <div
                  key={img.generatedImageIdx}
                  className="w-64 aspect-[3/4] rounded-xl overflow-hidden shadow-lg"
                >
                  <img
                    src={toImageUrl(img.imageUrl || img.s3Key || "")}
                    alt="체험 결과"
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => navigate("/")}
                className="px-8 py-3 border border-gray-200 text-gray-600 rounded-full text-sm hover:bg-gray-50 transition-colors"
              >
                홈으로
              </button>
              <button
                onClick={() => { gaEvent('click_cta', { location: 'trial_result', button: 'start_paid_plan' }); navigate("/order"); }}
                className="px-8 py-3 bg-[#C9A96E] text-white rounded-full text-sm font-medium hover:bg-[#b8935a] transition-colors"
              >
                유료 플랜 시작하기
              </button>
            </div>
          </div>
        )}

        {/* STEP: Already Used */}
        {step === "already-used" && (
          <div className="bg-white rounded-2xl border border-[#F0E8DC] p-8 text-center">
            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i
                className={`${resultUnavailable ? "ri-error-warning-line" : "ri-information-line"} text-3xl text-[#C9A96E]`}
              ></i>
            </div>
            <h2 className="font-serif text-2xl font-medium text-[#2D2D2D] mb-2">
              {resultUnavailable
                ? "체험 이미지 결과를 확인할 수 없습니다"
                : "이미 무료 체험을 사용하셨습니다"}
            </h2>
            <p className="text-sm text-gray-500 font-light mb-8">
              {resultUnavailable
                ? "생성은 종료됐지만 결과 이미지를 불러올 수 없습니다. 잠시 후 새로고침하거나 고객센터에 문의해주세요."
                : "무료 체험은 계정당 1회만 가능합니다. 이전에 생성된 결과물을 확인해보세요."}
            </p>

            {resultImages.length > 0 && (
              <div className="flex justify-center gap-4 mb-8">
                {resultImages.map((img) => (
                  <div
                    key={img.generatedImageIdx}
                    className="w-64 aspect-[3/4] rounded-xl overflow-hidden shadow-lg"
                  >
                    <img
                      src={toImageUrl(img.imageUrl || img.s3Key || "")}
                      alt="이전 체험 결과"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => navigate("/")}
                className="px-8 py-3 border border-gray-200 text-gray-600 rounded-full text-sm hover:bg-gray-50 transition-colors"
              >
                홈으로
              </button>
              {resultUnavailable ? (
                <button
                  onClick={() => window.location.reload()}
                  className="px-8 py-3 bg-[#C9A96E] text-white rounded-full text-sm font-medium hover:bg-[#b8935a] transition-colors"
                >
                  새로고침
                </button>
              ) : (
                <button
                  onClick={() => { gaEvent('click_cta', { location: 'trial_already_used', button: 'start_paid_plan' }); navigate("/order"); }}
                  className="px-8 py-3 bg-[#C9A96E] text-white rounded-full text-sm font-medium hover:bg-[#b8935a] transition-colors"
                >
                  유료 플랜 시작하기
                </button>
              )}
            </div>
          </div>
        )}

        {/* Info note */}
        {step !== "generating" && (
          <p className="text-center text-xs text-gray-400 font-light mt-8">
            * 무료 체험은 계정당 1회, 1장의 이미지가 제공됩니다. 로그인이 필요합니다.
          </p>
        )}
      </main>
    </div>
  );
}
